import { signal } from "@preact/signals";
import { type BackendContentItem, type BackendConversation, getAllContent, getAllConversations, getRecentContent } from "services/backend";
import { ContentItemType } from "../graphql/types";

const MAX_STARRED = 30;
const MAX_RECENT = 30;

interface _UserData {
    starredItems: BackendContentItem[]|null,
    recentItems: BackendContentItem[]|null,
    allItems: BackendContentItem[] | null;
}

export const globalUserData = signal<_UserData>({
    starredItems: null,
    recentItems: null,
    allItems: null,
});

export async function ensureGlobalUserDataItems(force = false) {
    if (!!globalUserData.peek().allItems && !force) return;

    const result = await getAllContent();
    if(result?.self) {
        globalUserData.value = {
            ...globalUserData.value,
            starredItems: result.self.contentItems.filter(n => n.starred).slice(0, MAX_STARRED),
            recentItems: result.self.contentItems.filter(n => !n.starred).slice(0, MAX_RECENT),
            allItems: result.self.contentItems
        };
    }
}

export function addConversation(id: string, subject: string, created = new Date()) {
    if (globalUserData.peek().recentItems && (globalUserData.peek().recentItems!.length == 0 || globalUserData.peek().recentItems![0].id !== id)) {
        const newItem: BackendContentItem =                 {
                    id,
                    type: ContentItemType.Chat,
                    title: subject,
                    created,
                    starred: false
                };
        globalUserData.value = {
            ...globalUserData.value,
            recentItems: [
                newItem,
                ... (globalUserData.value.recentItems! ?? [])
            ],
            allItems: [
                newItem,
                ...(globalUserData.value.allItems ?? [])
            ]
        }
    }
}

function _removeConversationOrItem(id: string, collection: keyof _UserData) {
    if (globalUserData.peek()[collection]) {
        const index = globalUserData.peek()[collection]!.findIndex(n => n.id == id) ?? -1;
        if (index != -1) {
            const newArray = [...globalUserData.value[collection]!];
            newArray.splice(index, 1);
            globalUserData.value = {
                ...globalUserData.value,
                [collection]: newArray
            }
        }
    }
}

export function removeConversation(id: string) {
    _removeConversationOrItem(id, "allItems");
    _removeConversationOrItem(id, "starredItems");
    _removeConversationOrItem(id, "recentItems");
}

function _setTitle(id: string, title: string, collection: keyof _UserData) {
    if (globalUserData.peek()[collection]) {
        const index = globalUserData.peek()[collection]!.findIndex(n => n.id == id) ?? -1;
        if (index != -1 && (globalUserData.peek()[collection]![index] as BackendContentItem).title !== title) {
            const items = [...globalUserData.value[collection]!];
            items[index] = {
                ...items[index],
                title
            };
            globalUserData.value = {
                ...globalUserData.value,
                [collection]: items
            };
        }
    }
}

export function setSubject(id: string, subject: string) {
    _setTitle(id, subject, "starredItems");
    _setTitle(id, subject, "recentItems");
    _setTitle(id, subject, "allItems");
}

export function setConversationStarred(id: string, starred: boolean) {
    const item = globalUserData.peek().allItems?.find(n => n.id == id);
    if(!item || item.starred == starred) return;

    const items = [...globalUserData.peek().allItems!];
    const index = items.indexOf(item);
    items[index] = {
        ...items[index],
        starred
    };

    // todo: only update starredConversations and recentConversations if they actually change

    globalUserData.value = {
        ...globalUserData.value,
        starredItems: items.filter(n => n.starred).slice(0, MAX_STARRED),
        recentItems: items.filter(n => !n.starred).slice(0, MAX_RECENT),
        allItems: items
    };
}
